import { api } from './client';

/**
 * ============================================================================
 * THE 24TV CLIENT — READ-ONLY, AND THAT IS A RULE (S23(b); Appendix L §3, F31)
 * ============================================================================
 *
 *   GET /pa/tv/24tv?contract_id=[&account_id=]   — one row per Account of the contract
 *
 * It mirrors the wire structs in `backend/internal/personalarea/tv.go`, pinned BOTH WAYS to
 * `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ THERE IS NO WRITE CALL IN THIS FILE AND THERE MUST NOT BE ONE
 * ============================================================================
 *
 * Appendix L §3 and F31 describe connect / disconnect / replace-main / «Отмена перехода» for
 * an Account governed by the FULL 24TV integration mode. `answers.md` P6-T7/P6-T8 suspended
 * that mode until after the production launch and the build REFUSES to save an instance in
 * it — so on every 24TV Account this installation has, those controls would be buttons that
 * control nothing: the D109 dead-control class on the one screen where the operator never
 * sees the failure, because the subscriber simply concludes the portal is broken.
 * Questionnaire S23's adopted answer (b) is therefore the rule and not a preference.
 *
 * `switching_managed_externally` carries that as a FACT rather than as prose: the day the
 * full mode is un-suspended the field becomes false and the switching controls arrive with
 * it, instead of a comment somebody has to find.
 *
 * ============================================================================
 * ⚠ `state: "activation_needed"` IS THE MONTHLY HOLE, AND IT IS THE POINT OF THE SECTION
 * ============================================================================
 *
 * The bundle is pushed when 24TV calls `login` — i.e. when the subscriber OPENS THE
 * APPLICATION — and the window is the calendar month. So an ENTITLED subscriber who has not
 * opened it in a new month has no television until he does. A blank empty state cannot say
 * that, and «где моё ТВ?» is exactly the telephone call this section exists to prevent. The
 * state also covers a claim stranded at «pending» (D251) and one that failed, because
 * opening the application re-arms the push in all three cases.
 *
 * EVERY READ IS LOCAL POSTGRES. The only way to learn his real STANDALONE subscriptions is a
 * live platform call with no rate limiter behind it, so what this answers with is the COMBO
 * entitlement and this month's window; packages bought inside the application are stored
 * nowhere in this system and the screen says so rather than implying an empty history.
 *
 * Deliberately absent from the wire, and not to be «completed» later: `mbr_id` (a third
 * party's primary key is not part of «his television»), the provisioning `detail` and a
 * purchase's `message` (both provider-authored text).
 */

/** `PATVPacket` — one 24TV package. */
export interface PATVPacket {
  packet_id: number;
  /**
   * ⚠ NULL MEANS THE OPERATOR HAS NEVER FETCHED THE CATALOGUE — it is not an empty name.
   * The screen renders «Пакет №N» from the id, never «null» and never a blank: the same
   * rule `address_known` states on the device wire, applied to a label.
   */
  name: string | null;
}

/** `PATVWindow` — THIS calendar month's subscription window. */
export interface PATVWindow {
  start_at: string;
  /** 23:59:59 of the month's last day — the legacy's own boundary, kept deliberately. */
  end_at: string;
}

/** `PATVPurchase` — one row of his own purchase history. */
export interface PATVPurchase {
  at: string;
  packet: PATVPacket;
  /**
   * MINOR UNITS, and ZERO for a BUNDLED activation. The row exists so the activation stays
   * traceable, and hiding it would make a combo subscriber's history look empty in a month
   * he did use his television.
   */
  amount: number;
}

/** `PATVAccount` — one Account's television, as its owner may see it. */
export interface PATVAccount {
  account_id: number;
  login: string;
  /**
   * DERIVED on the server; the vocabulary is closed and each value has ONE clear next
   * action. «unavailable» — this installation has no usable 24TV partnership (not a fact
   * about him); «not_entitled» — his tariff bundles no package; «active» — this month's
   * subscription was pushed and `window` carries the dates; «activation_needed» — THE
   * MONTHLY HOLE, see the header.
   */
  state: string;
  /** Whether this Account has ever met 24TV. FALSE IS A NORMAL STATE. */
  linked: boolean;
  last_seen_at: string | null;
  /** The package his tariff bundles, or null when it bundles none. */
  combo: PATVPacket | null;
  /** Null when nothing was pushed for this month — which is what `activation_needed` means. */
  window: PATVWindow | null;
  purchases: PATVPurchase[];
}

/** `PATelevision` — the section: one row per Account of the contract, READ-ONLY. */
export interface PATelevision {
  contract_id: number;
  items: PATVAccount[];
  total: number;
  /** TRUE on this build. It IS the cross-note — see the header. */
  switching_managed_externally: boolean;
}

/**
 * getTelevision reads the contract's 24TV picture.
 *
 * The contract travels in the query string because `Service.moneyScope` is the ONE ownership
 * check on this listener and reads `?contract_id=` — a request without it is answered 404,
 * indistinguishably from «not yours», so the failure would read as a permissions fault
 * rather than as a client bug.
 */
export function getTelevision(contractID: number): Promise<PATelevision> {
  return api.get<PATelevision>(`/pa/tv/24tv?contract_id=${contractID}`);
}
